import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router";
import { appPaths, WORKFLOW_ADD_PARAMETER, WORKFLOW_THREAD_PARAMETER } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import { parseDocumentVersionRef } from "../../../shared/references/contentReferences";
import { useProjects } from "../../../projects/projectContext";
import CopyReference from "../../../ui/content/CopyReference";
import { useProcess } from "../data/useProcess";
import { useWorkflowPlans } from "../data/useWorkflowPlans";
import EditAddressRedirect from "../../threads/prepare/author/EditAddressRedirect";
import { EDIT_MODE, PROCESS_MODE_PARAMETER } from "../view/processDisplay";
import ProcessView from "../view/ProcessView";
import { useProcessViewChoices } from "../view/useProcessViewChoices";
import WorkflowActions from "./WorkflowActions";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { usePaneRedirect, useTabTitle } from "../../../shell/workspace";

/**
 * `/workflows/<item id>@<version>`: one Workflow version, pipeline first (P-14). A Run of it
 * has its own graph (`/threads/<id>/graph`, WD-4); the old `?thread=<id>` address opens that.
 */
export default function WorkflowPage() {
  const { workflowAddress = "" } = useParams();
  // A Workflow is always one exact version (FR-WF-2); the form is the same as a document's.
  if (parseDocumentVersionRef(workflowAddress) === null) {
    return <ErrorNotice error={{ kind: "invalidAddress", address: appPaths.workflow(workflowAddress) }} className="py-6" />;
  }
  return <WorkflowVersion key={workflowAddress} reference={workflowAddress} />;
}

function WorkflowVersion({ reference }: { reference: string }) {
  const [searchParams] = useSearchParams();
  // Edit mode's old address opens the edit's preparation, by hand (ADR-0031 D2, CP-06).
  if (searchParams.get(PROCESS_MODE_PARAMETER) === EDIT_MODE) {
    return <EditAddressRedirect reference={reference} carry={searchParams.get(WORKFLOW_ADD_PARAMETER)} />;
  }
  const run = searchParams.get(WORKFLOW_THREAD_PARAMETER);
  if (run !== null) {
    return <RunGraphRedirect threadId={run} />;
  }
  return <WorkflowViewOf reference={reference} />;
}

/** The Workflow view's old address of a Run (F103): the run graph is that Run's own page now (WD-4). */
function RunGraphRedirect({ threadId }: { threadId: string }) {
  const redirect = usePaneRedirect();
  useEffect(() => { redirect(appPaths.runGraph(threadId)); }, [redirect, threadId]);
  return null;
}

function WorkflowViewOf({ reference }: { reference: string }) {
  const { t } = useTranslation();
  const choices = useProcessViewChoices();
  const projectId = useProjects().selectedProjectId;
  const { state, reload } = useProcess(reference, null);
  const plans = useWorkflowPlans(reference, null);
  useTabTitle(state.status === "loaded" ? { kind: "workflow", name: state.value.workflow.name } : null);

  return (
    <div className="space-y-4 py-4">
      <LoadStatus state={state} loadingLabel={t("workflows.view.loading")} loadingClassName="py-10" onRetry={reload} />
      {state.status === "loaded" ? (
        <>
          <header className="space-y-1">
            <h2 className="app-title text-xl font-bold">{state.value.workflow.name}</h2>
            {state.value.workflow.description === "" ? null : <p className="app-text-muted max-w-3xl text-sm">{state.value.workflow.description}</p>}
            <p className="app-text-muted flex items-center gap-1 text-xs">
              <Link to={appPaths.item(reference)} className="app-link underline">{t("workflows.view.definition")}</Link>
              <CopyReference value={reference} />
            </p>
            {projectId === null ? null : (
              <WorkflowActions projectId={projectId} reference={reference} name={state.value.workflow.name} />
            )}
          </header>
          <ProcessView
            process={state.value}
            plans={plans}
            display={choices.display}
            onDisplayChange={choices.setDisplay}
            density={choices.density}
            onDensityChange={choices.setDensity}
            layout={choices.layout}
            onLayoutChange={choices.setLayout}
            run={null}
            stepLinkInThread={null}
            canvas="page"
            contextProjectId={projectId}
          />
        </>
      ) : null}
    </div>
  );
}
